import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type FamilyRelationship = Database["public"]["Enums"]["family_relationship"];

export interface FamilyMember {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  relationship: FamilyRelationship;
  birth_date: string | null;
  avatar_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type FamilyMemberInput = Omit<FamilyMember, "id" | "user_id" | "created_at" | "updated_at">;

export function useFamilyMembers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all family members
  const { data: members = [], isLoading, error } = useQuery({
    queryKey: ["family-members", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("family_members")
        .select("*")
        .eq("user_id", user.id)
        .order("relationship", { ascending: true })
        .order("first_name", { ascending: true });

      if (error) throw error;
      return data as FamilyMember[];
    },
    enabled: !!user?.id,
  });

  // Create family member
  const createMember = useMutation({
    mutationFn: async (input: FamilyMemberInput) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("family_members")
        .insert({
          user_id: user.id,
          first_name: input.first_name,
          last_name: input.last_name,
          relationship: input.relationship,
          birth_date: input.birth_date,
          notes: input.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-members"] });
      toast.success("Membre ajouté");
    },
    onError: (error: Error) => {
      console.error("Create member error:", error);
      toast.error("Erreur lors de l'ajout");
    },
  });

  // Update family member
  const updateMember = useMutation({
    mutationFn: async ({ id, ...input }: Partial<FamilyMemberInput> & { id: string }) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("family_members")
        .update({
          first_name: input.first_name,
          last_name: input.last_name,
          relationship: input.relationship,
          birth_date: input.birth_date,
          notes: input.notes,
        })
        .eq("id", id)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-members"] });
      toast.success("Membre mis à jour");
    },
    onError: (error: Error) => {
      console.error("Update member error:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  // Delete family member
  const deleteMember = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("family_members")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-members"] });
      queryClient.invalidateQueries({ queryKey: ["essential-doc-links"] });
      toast.success("Membre supprimé");
    },
    onError: (error: Error) => {
      console.error("Delete member error:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  return {
    members,
    isLoading,
    error,
    createMember,
    updateMember,
    deleteMember,
  };
}

// Relationship labels in French
export const RELATIONSHIP_LABELS: Record<FamilyRelationship, string> = {
  self: "Moi-même",
  spouse: "Conjoint(e)",
  child: "Enfant",
  parent: "Parent",
  sibling: "Frère/Sœur",
  other: "Autre",
};

// Relationship icons
export const RELATIONSHIP_ICONS: Record<FamilyRelationship, string> = {
  self: "👤",
  spouse: "💑",
  child: "👶",
  parent: "👴",
  sibling: "👫",
  other: "👥",
};
